import { useQuery } from "@tanstack/react-query"
import { ScrollText } from "lucide-react"
import { useRef, useState } from "react"
import { api } from "@/api/cliente"
import { Exportar } from "@/componentes/exportar/Exportar"
import { Cabecera, Cuerpo, Fila, Tabla, Td, Th } from "@/componentes/ui/Tabla"
import { comoFechaHora } from "@/lib/fechas"

/**
 * Consulta de la bitácora.
 *
 * **Sólo lectura, y no por comodidad: la bitácora es append-only.** No existe
 * endpoint que la edite o la borre, y un listener de SQLAlchemy rechaza `UPDATE` y
 * `DELETE` sobre esa tabla. Así que esta pantalla no tiene —ni puede tener— un
 * botón de editar: sería una acción sin destino.
 *
 * `antes` y `despues` llegan **ya redactados** desde el servidor: toda clave que
 * contenga «password», «token», «secret», «authorization» o «clave» viaja como
 * `***`. No se redacta aquí; hacerlo daría una segunda implementación de la regla
 * que se separaría de la primera.
 */

interface FilaAuditoria {
  id: number
  actor_id: number | null
  accion: string
  recurso_tipo: string
  recurso_id: number | null
  antes: Record<string, unknown> | null
  despues: Record<string, unknown> | null
  ip: string | null
  creado_en: string
}

interface PaginaAuditoria {
  items: FilaAuditoria[]
  total: number
  limit: number
  offset: number
}

const POR_PAGINA = 25

/** Los recursos que hoy escriben bitácora. */
const RECURSOS = [
  "usuarios",
  "cotizaciones",
  "expedientes",
  "expediente_documentos",
  "tareas",
  "hilos",
  "mensajes",
]

function Cambio({ etiqueta, datos }: { etiqueta: string; datos: Record<string, unknown> | null }) {
  if (datos === null) return null
  return (
    <div>
      <span className="text-xs font-medium uppercase" style={{ color: "var(--fg-muted)" }}>
        {etiqueta}
      </span>
      <ul className="mt-0.5">
        {Object.entries(datos).map(([clave, valor]) => (
          <li key={clave} className="text-xs">
            <span style={{ color: "var(--fg-muted)" }}>{clave}:</span>{" "}
            {/* Texto plano siempre. El contenido lo escribió un usuario. */}
            <span className="tabular">{String(valor)}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

export function Auditoria() {
  const [offset, setOffset] = useState(0)
  const [recurso, setRecurso] = useState("")

  const parametros = new URLSearchParams({ limit: String(POR_PAGINA), offset: String(offset) })
  if (recurso) parametros.set("recurso_tipo", recurso)

  const consulta = useQuery({
    queryKey: ["auditoria", { offset, recurso }],
    queryFn: () => api.get<PaginaAuditoria>(`/api/v1/auditoria?${parametros}`),
  })

  const pagina = consulta.data
  const hasta = pagina ? Math.min(pagina.offset + pagina.limit, pagina.total) : 0
  const vista = useRef<HTMLDivElement | null>(null)
  const subtitulo = [
    recurso ? `Recurso: ${recurso.replaceAll("_", " ")}` : "Todos los recursos",
    pagina && pagina.total > 0 ? `${pagina.offset + 1}–${hasta} de ${pagina.total}` : null,
  ]
    .filter(Boolean)
    .join(" · ")

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Auditoría</h1>
          <p className="mt-1 text-sm" style={{ color: "var(--fg-muted)" }}>
            {pagina ? `${pagina.total} registros` : " "}
          </p>
        </div>
        {pagina && pagina.items.length > 0 && (
          <Exportar titulo="Auditoría" subtitulo={subtitulo} contenido={vista} />
        )}
      </div>

      <div className="mb-4">
        <label htmlFor="filtro-recurso" className="mr-2 text-sm">
          Recurso
        </label>
        <select
          id="filtro-recurso"
          value={recurso}
          onChange={(e) => {
            setRecurso(e.target.value)
            setOffset(0)
          }}
          className="h-9 rounded-[var(--radius-control)] border px-2 text-sm"
          style={{
            borderColor: "var(--border)",
            backgroundColor: "var(--surface)",
            color: "var(--fg)",
          }}
        >
          <option value="">Todos</option>
          {RECURSOS.map((r) => (
            <option key={r} value={r}>
              {r.replaceAll("_", " ")}
            </option>
          ))}
        </select>
      </div>

      {consulta.isPending && (
        <p className="py-12 text-center text-sm" style={{ color: "var(--fg-muted)" }}>
          Cargando la bitácora…
        </p>
      )}

      {consulta.isError && (
        <p role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
          No se pudo cargar la bitácora
        </p>
      )}

      {pagina && pagina.items.length === 0 && (
        <div
          className="flex flex-col items-center gap-3 superficie py-16"
          style={{ color: "var(--fg-muted)" }}
        >
          <ScrollText size={24} aria-hidden="true" />
          <p className="text-sm">No hay registros para ese filtro</p>
        </div>
      )}

      {pagina && pagina.items.length > 0 && (
        <div ref={vista}>
          <Tabla etiqueta="Bitácora de auditoría">
            <Cabecera>
              <Th>Cuándo</Th>
              <Th>Actor</Th>
              <Th>Acción</Th>
              <Th>Recurso</Th>
              <Th>Cambio</Th>
              <Th>IP</Th>
            </Cabecera>
            <Cuerpo>
              {pagina.items.map((f) => (
                <Fila key={f.id}>
                  <Td numero className="whitespace-nowrap">
                    {comoFechaHora(f.creado_en, true)}
                  </Td>
                  <Td numero>{f.actor_id ?? "—"}</Td>
                  <Td className="font-medium">{f.accion}</Td>
                  <Td style={{ color: "var(--fg-muted)" }}>
                    {f.recurso_tipo}
                    <span className="tabular"> #{f.recurso_id ?? "—"}</span>
                  </Td>
                  <Td>
                    <div className="grid gap-2 md:grid-cols-2">
                      <Cambio etiqueta="antes" datos={f.antes} />
                      <Cambio etiqueta="después" datos={f.despues} />
                    </div>
                  </Td>
                  <Td numero style={{ color: "var(--fg-muted)" }}>
                    {f.ip ?? "—"}
                  </Td>
                </Fila>
              ))}
            </Cuerpo>
          </Tabla>
        </div>
      )}

      {pagina && pagina.total > pagina.limit && (
        <div className="mt-4 flex items-center justify-between text-sm">
          <span className="tabular" style={{ color: "var(--fg-muted)" }}>
            {pagina.offset + 1}–{hasta} de {pagina.total}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - POR_PAGINA))}
              className="h-9 rounded-[var(--radius-control)] border px-3 disabled:opacity-40"
              style={{ borderColor: "var(--border)" }}
            >
              Anterior
            </button>
            <button
              type="button"
              disabled={hasta >= pagina.total}
              onClick={() => setOffset(offset + POR_PAGINA)}
              className="h-9 rounded-[var(--radius-control)] border px-3 disabled:opacity-40"
              style={{ borderColor: "var(--border)" }}
            >
              Siguiente
            </button>
          </div>
        </div>
      )}

      <p className="mt-4 text-xs" style={{ color: "var(--fg-muted)" }}>
        La bitácora es append-only: no se edita ni se borra desde ninguna parte del sistema. Los
        valores sensibles llegan ya redactados como <code>***</code> desde el servidor.
      </p>
    </div>
  )
}
